import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const renderApi = axios.create({
  baseURL: process.env.REACT_APP_RENDER_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
// api.interceptors.request.use(
//   (config) => {
//     // No need to manually add auth token - cookies are sent automatically
//     // due to withCredentials: true
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );

// Response interceptor
// api.interceptors.response.use(
//   (response) => {
//     return response;
//   },
//   (error) => {
//     // Don't automatically redirect on 401 - let the AuthContext handle it
//     // This prevents unwanted redirects during auth checks
//     return Promise.reject(error);
//   }
// );

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/login', { email, password }),

  signup: (name, email, password, passwordConfirm) =>
    api.post('/signup', { name, email, password, passwordConfirm }),

  googleAuth: (credential) => api.post('/auth/google', { credential }),

  logout: () => api.post('/logout'),

  getMe: () => api.get('/users/profile'),

  updateMe: (data) => api.patch('/users/profile', data),

  updatePassword: (passwordCurrent, password, passwordConfirm) =>
    api.patch('/update-password', {
      passwordCurrent,
      password,
      passwordConfirm,
    }),

  setPassword: (password, passwordConfirm) =>
    api.patch('/update-password', {
      password,
      passwordConfirm,
    }),

  // Email verification
  verifyEmail: (token) => api.get(`/verify-email/${token}`),

  resendVerificationEmail: () => api.post('/resend-verification'),

  checkVerificationStatus: () => api.get('/verification-status'),

  // forgotPassword: (email) =>
  //   api.post('/auth/forgot-password', { email }),

  // resetPassword: (token, password, passwordConfirm) =>
  //   api.patch(`/auth/reset-password/${token}`, {
  //     password,
  //     passwordConfirm,
  //   }),
};

// Templates API
export const templatesAPI = {
  getAll: (params = {}) => api.get('/templates', { params }),

  getById: (id) => api.get(`/templates/${id}`),

  getPopular: (limit = 10) => api.get(`/templates/popular?limit=${limit}`),

  getCategories: () => api.get('/templates/categories'),

  getByCategory: (category, limit = 20) =>
    api.get(`/templates/category/${category}?limit=${limit}`),

  addReview: (id, rating, comment) =>
    api.post(`/templates/${id}/review`, { rating, comment }),

  incrementDownload: (id) => api.patch(`/templates/${id}/download`),

  // Publish project as template (admin only)
  publishTemplate: (data) => api.post('/templates/publish', data),

  // Delete template (admin only)
  deleteTemplate: (id) => api.delete(`/templates/${id}`),
  delete: (id) => api.delete(`/templates/${id}`), // Alias for deleteTemplate

  // Increment usage count
  incrementUsage: (id) => api.patch(`/templates/${id}/use`),

  // Get template by project ID (to check if project has been published)
  getByProjectId: (projectId) => api.get(`/templates/by-project/${projectId}`),

  // Update template with preview video after export completes (admin only)
  updatePreviewVideo: (templateId, jobId) =>
    api.post('/templates/preview/update', { templateId, jobId }),
};

// Ag Components API to fetch design components
export const agComponentsAPI = {
  getAll: (params = {}) => api.get('/agComponents', { params }),

  getById: (id) => api.get(`/agComponent/${id}`),
};

// Jobs API to render components from user inputs
export const jobsAPI = {
  getById: (id) =>
    api.get(`/jobs/${id}`),

  submitJob: (data) =>
    api.patch('/submit-job', data),

  getUpdate: (componentId) => {
    // Return a function that creates an EventSource
    const eventSource = new EventSource(`${process.env.REACT_APP_API_URL || '/api/v1'}/render-status/${componentId}`, {
      withCredentials: true
    });

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
    };

    return eventSource;
  },

  patchJobUpdates: (componentId, AeId) =>
    api.patch(`/patch-job-updates/${componentId}`, { AeId }),

  delete: (id) =>
    api.delete(`/jobs/${id}`),

  retry: (id) =>
    api.patch(`/jobs/${id}/retry`),

  download: (id) =>
    api.get(`/jobs/${id}/download`),

  getStats: () =>
    api.get('/jobs/stats'),
};

// API's for interfacing with the server on what components are on Canvas
export const componentToCanvasAPI = {
  // Create a component render
  addComponent: (componentId, componentUrl) =>
    api.post('/add-component', { componentId, componentUrl }),

  //Get a component's render path
  getComponentRenderPath: (componentId) =>
    api.get(`/render-path/${componentId}`),

  // Check if render exists
  checkRenderExists: async (componentId, jobUid) => {
    try {
      const response = await api.get(`/add-component/${jobUid}/${componentId}`);
      return response.data.renderPath ? true : false;
    } catch (error) {
      return false;
    }
  },
};

// Projects API
export const projectsAPI = {
  // Create a new project with default values
  createNewProject: (projectData) =>
    api.post('/projects/create-project', projectData),

  // Upload thumbnail
  uploadThumbnail: (thumbnailBlob, projectId, filename) => {
    const formData = new FormData();
    formData.append('thumbnail', thumbnailBlob, filename);
    formData.append('projectId', projectId);
    formData.append('filename', filename); // Pass filename to server for consistent naming

    return api.post('/projects/upload-thumbnail', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Save project with metadata
  saveProject: (projectData) =>
    api.post('/projects', projectData),

  //Change Project Name
  changeProjectName: (projectId, name) =>
    api.patch(`/projects/${projectId}`, { name }),

  // Get all projects for logged-in user
  getAllProjects: () =>
    api.get('/projects'),

  // Get single project
  getProject: (id) =>
    api.get(`/projects/${id}`),

  // Delete project
  deleteProject: (id) =>
    api.delete(`/projects/${id}`),

  // Initialize Export Job
  initializeExport: async (data) => {
    return await api.post('/exports/initialize', data, {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
  },

  // Get Export Status
  getExportStatus: async (params) => {
    return await api.get('/exports/status', {
      params,
      timeout: 10000
    });
  },

  // Cancel Export Job
  cancelExport: async (data) => {
    return await api.post('/exports/cancel', data, {
      timeout: 10000
    });
  },

  initializePuppeteerExport: async (exportData) => {
    return api.post('/exports/puppeteer/initialize', exportData);
  },

  getPuppeteerExportStatus: async (params) => {
    return api.get('/exports/puppeteer/status', { params });
  },

  cancelPuppeteerExport: async (data) => {
    return api.post('/exports/puppeteer/cancel', data);
  },
};

// Users API
export const usersAPI = {
  getProfile: () => api.get('/users/profile'),

  updateProfile: (data) => api.patch('/users/profile', data),

  updatePreferences: (preferences) =>
    api.patch('/users/preferences', { preferences }),

  getStats: () => api.get('/users/stats'),
};

// Media API
export const mediaAPI = {
  // Upload media with metadata extraction
  uploadMedia: (file, data = {}, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    // Add optional metadata
    if (data.description) formData.append('description', data.description);
    if (data.tags) formData.append('tags', data.tags);

    return api.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && typeof onProgress === 'function') {
          onProgress(progressEvent);
        }
      },
    });
  },

  // Get user's uploaded media
  getMyMedia: (params = {}) => api.get('/media/my-media', { params }),

  // Get user's AI-generated media
  getAiMedia: (params = {}) => api.get('/media/ai-generated', { params }),

  // Get single media
  getMedia: (id) => api.get(`/media/${id}`),

  // Update media metadata
  updateMedia: (id, data) => api.patch(`/media/${id}`, data),

  // Delete media
  deleteMedia: (id) => api.delete(`/media/${id}`),

  // Get public media
  getPublicMedia: (params = {}) => api.get('/media/public', { params }),

  // Process media with crop/trim
  processCropTrim: (data) => api.post('/media/process-crop-trim', data),

  // Transcribe media
  transcribeMedia: (url) => api.post('/media/transcribe', { url }),
};

// AI Generation API
export const aiAPI = {
  // Submit a new generation job
  generate: (data) => api.post('/ai/generate', data),

  // Poll job status
  getJobStatus: (jobId) => api.get(`/ai/job/${jobId}`),

  // Get the latest job for a sprite (reconnect after page refresh)
  getActiveJobForSprite: (spriteId) => api.get(`/ai/sprite/${spriteId}/active`),

  // Get the last N completed jobs for a sprite (generation history)
  getJobHistory: (spriteId, limit = 3) => api.get(`/ai/sprite/${spriteId}/history`, { params: { limit } }),

  // User selects one of the generated results
  selectResult: (jobId, resultIndex) => api.post(`/ai/job/${jobId}/select`, { resultIndex }),

  // Cancel an in-progress or queued job
  cancelJob: (jobId) => api.post(`/ai/job/${jobId}/cancel`),

  // Get current user's credit balance
  getCredits: () => api.get('/ai/credits'),

  // Estimate credit cost for a model + prompt (server-side)
  estimateCost: (model, prompt) => api.post('/ai/estimate', { model, prompt }),
};

// Blog API
export const blogAPI = {
  // Public - get published blogs
  getPublished: (params = {}) => api.get('/blogs/published', { params }),

  // Public - get single published blog by slug
  getBySlug: (slug) => api.get(`/blogs/published/${slug}`),

  // Admin/Editor - get all blogs (drafts + published)
  getAll: (params = {}) => api.get('/blogs/manage', { params }),

  // Admin/Editor - get single blog by id
  getById: (id) => api.get(`/blogs/manage/${id}`),

  // Admin/Editor - create blog (supports FormData for thumbnail)
  create: (formData) =>
    api.post('/blogs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Admin/Editor - update blog (supports FormData for thumbnail)
  update: (id, formData) =>
    api.patch(`/blogs/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Admin/Editor - delete blog
  delete: (id) => api.delete(`/blogs/${id}`),
};

// Payments API
export const paymentsAPI = {
  // Get available plans (public)
  getPlans: () => api.get('/payments/plans'),

  // Create a Stripe Checkout session (returns URL to redirect to)
  createCheckoutSession: (plan, interval) =>
    api.post('/payments/create-checkout-session', { plan, interval }),

  // Get current subscription status
  getSubscription: () => api.get('/payments/subscription'),

  // Create Stripe Customer Portal session (returns URL)
  createCustomerPortal: () => api.post('/payments/customer-portal'),

  // Cancel subscription at period end
  cancelSubscription: () => api.post('/payments/cancel-subscription'),

  // Resume a subscription set to cancel
  resumeSubscription: () => api.post('/payments/resume-subscription'),

  // Change subscription plan (upgrade/downgrade)
  changeSubscription: (plan, interval) =>
    api.post('/payments/change-subscription', { plan, interval }),
};

export default api;
